import { cn } from "@/lib/cn";
import { shimmer, skeletonSurface } from "@/lib/shimmer";

/**
 * Leaderboard row placeholder — rank pill + avatar + two name lines + score.
 * Default: 8 rows to match a typical Top-8 ranking below the podium.
 */
export function RankingSkeleton({
  rows = 8,
  className,
}: {
  rows?: number;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-2", className)} aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <RankingRow key={i} index={i} />
      ))}
    </div>
  );
}

function RankingRow({ index }: { index: number }) {
  // Give earlier rows slightly wider "name" lines so it visually resembles
  // real data variance (top scorers usually have longer visible content).
  const nameWidth = 140 + ((index * 17) % 80);
  const teamWidth = 60 + ((index * 11) % 40);

  return (
    <div
      className={cn(
        "relative flex items-center gap-4 rounded-[14px] border border-white/5 px-4 py-3",
        skeletonSurface,
        shimmer,
      )}
      style={{ height: 60 }}
    >
      {/* Rank pill */}
      <div className="h-7 w-9 shrink-0 rounded-lg bg-white/[0.06] ring-1 ring-white/5" />
      {/* Avatar */}
      <div className="h-9 w-9 shrink-0 rounded-full bg-white/5 ring-1 ring-white/10" />
      {/* Name + team stack */}
      <div className="flex flex-col gap-1.5">
        <div
          className="h-3 rounded bg-white/[0.08]"
          style={{ width: nameWidth }}
        />
        <div
          className="h-2 rounded bg-white/[0.04]"
          style={{ width: teamWidth }}
        />
      </div>
      {/* Score on the right */}
      <div className="ml-auto h-4 w-14 rounded bg-white/[0.06]" />
    </div>
  );
}
